import React, { useEffect, FC, useState, ReactNode, useContext, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { SOCKET_SUB_TYPE, TRADING_TYPE } from 'src/common/constants';
import {
    AreaChart,
    BarChart,
    BaselineChart,
    CandleChart,
    ChartCamera,
    ChartExpand,
    ChartIndicator,
    ChartSetting,
    ColumnsChart,
    DropDownArrow,
    Favorite,
    Favorites,
    HeikinAshiChart,
    HighLowChart,
    HollowChart,
    LineChart,
    Minus,
    Plus,
} from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch } from 'src/hooks';
import { ResolutionString } from 'src/lib/charting_library/charting_library';
import {
    updateChartType,
    updateFavoriteIntervals,
    updateFavoriteChartTypes,
} from 'src/setup/redux/slices/globalSlice';
import SocketContext from 'src/setup/socket/socketContext';

interface ChartHeaderProps {
    activeTab?: number;
    activeChart?: string;
    tvWidgetRef: any;
    favoriteChartTypes: string[];
    activeChartType: number;
    favoriteIntervals: ResolutionString[];
    chartInterval: string;
    tradeType: string;
}

const StyledChartHeader = styled(FlexContainer)`
    justify-content: space-between;
    padding: 0 24px;
    width: 100%;
    height: 32px;
    gap: 16px;
    overflow-x: scroll;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const ChartMenu = styled(FlexContainer)`
    height: 100%;
    gap: 16px;
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.tertiary};
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[2]};
    `}
`;

const Bar = styled.div`
    width: 1px;
    height: 12px;
    ${({ theme: { colors } }) => `
        background: ${colors.text.tertiary};
    `}
`;

const ChartIndicators = styled(FlexContainer)`
    gap: 8px;
    cursor: pointer;
    ${({ theme: { colors } }) => `
        &:hover {
            color: ${colors.primary};
            & > svg > path {
                stroke: ${colors.primary};
            }
        }
    `};
`;

const ChartIntervals = styled(FlexContainer)<{ isIntervalDropdownVisible?: boolean }>`
    height: 100%;
    gap: 8px;
    & > div:last-of-type {
        height: 100%;
        cursor: pointer;
    }
    ${({ theme: { colors }, isIntervalDropdownVisible }) => `
        & span: hover {
            color: ${colors.primary};
        }
        & > div:last-of-type {
            &:hover {
                & > svg {
                    transform: rotate(180deg);
                    & > path {
                        stroke: ${colors.primary};
                    }
                    & > circle {
                        fill: ${colors.primary};
                    }
                }
                & > div {
                    display: ${isIntervalDropdownVisible ? 'flex' : 'none'};
                }
            }
        }
    `};
`;

const FavoriteInterval = styled.span<{ color: string }>`
    cursor: pointer;
    ${({ color }) => `
        color: ${color};
    `}
`;

const IntervalResolutionDropDown = styled(FlexContainer)`
    width: 340px;
    flex-direction: column;
    align-items: flex-start;
    position: absolute;
    z-index: 15000;
    top: 32px;
    padding: 10px 20px;
    border-radius: 8px;
    gap: 10px;
    ${({ theme: { colors, typography } }) => `
        display: none;
        color: ${colors.text.secondary};
        background-color: ${colors.background.secondary.v3};
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[0]};
    `}
`;

const IntervalTitle = styled.div<{ color?: string }>`
    &:first-of-type {
        margin-top: 10px;
    }
    text-transform: uppercase;
    ${({ theme: { colors }, color }) => `
        color: ${color ? color : colors.text.tertiary};
    `}
`;

const IntervalContainer = styled(FlexContainer)`
    width: 100%;
    gap: 8px;
    flex-wrap: wrap;
`;

const Interval = styled(FlexContainer)`
    position: relative;
    padding: 8px 0;
    width: 69px;
    justify-content: center;
    border-radius: 4px;
    ${({ theme: { colors } }) => `
        border: 1px solid ${colors.border.dark};
        color: ${colors.text.primary};
        &.active {
            border: 1px solid ${colors.brand.main};
            background-color: ${colors.brand.main};
            color: ${colors.text.accent};
        }
        & > svg {
            position: absolute;
            top: -5px;
            right: -6px;
        }
    `}
`;

const CandleBarTypes = styled(FlexContainer)`
    height: 100%;
    gap: 8px;
    & svg,
    & > div {
        cursor: pointer;
        height: 100%;
    }
    ${({ theme: { colors } }) => `
        & > div {
            &.active > svg {
                & > path {
                    stroke: ${colors.primary};
                }
                & > circle {
                    fill: ${colors.primary};
                }
            }
            &:hover {
                &:last-of-type > svg {
                    transform: rotate(180deg);
                }
                & > svg {
                    & > path {
                        stroke: ${colors.primary};
                    }
                    & > circle {
                        fill: ${colors.primary};
                    }
                }
                & > div {
                    display: flex;
                }
            }
        }
    `};
`;

const CandleBarDropdown = styled(FlexContainer)`
    width: 180px;
    flex-direction: column;
    position: absolute;
    z-index: 15000;
    top: 32px;
    padding: 10px 0;
    border-radius: 8px;
    ${({ theme: { colors } }) => `
        display: none;
        background-color: ${colors.background.secondary.v3};
    `}
`;

const CandleBarDropdownItem = styled(FlexContainer)`
    width: 100%;
    padding: 10px 20px;
    justify-content: space-between;
    & > div {
        gap: 10px;
    }
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.secondary};
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[0]};
        background-color: ${colors.background.secondary.v3};
        &.active, &.active:hover {
            background-color: ${colors.primary};
            color: ${colors.text.accent};
            & > div > svg {
                & > path {
                    stroke: ${colors.text.accent};
                }
                & > circle {
                    fill: ${colors.text.accent};
                }
            }
        }
        &:hover {
            background-color: ${colors.background.secondary.v4};
            & > svg:not(.favorites) > path {
                stroke: ${colors.text.tertiary};
            } 
        }
    `}
`;

interface CandleBars {
    [key: number]: {
        id: string;
        icon: ReactNode;
        title: ReactNode;
        type: number;
    };
}

const ChartHeader: FC<ChartHeaderProps> = ({
    activeTab,
    activeChart,
    tvWidgetRef,
    favoriteChartTypes,
    activeChartType,
    favoriteIntervals,
    chartInterval,
    tradeType,
}) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [isEditInterval, setIsEditInterval] = useState(false);
    const [currentFavoriteIntervals, setCurrentFavoriteIntervals] = useState(favoriteIntervals);
    const [isIntervalDropdownVisible, setIsIntervalDropdownVisible] = useState(true);
    const dispatch = useAppDispatch();
    const { cancelTopicSubscription } = useContext(SocketContext);

    const intervals = [
        {
            resolution: '1',
            title: '1m',
        },
        {
            resolution: '5',
            title: '5m',
        },
        {
            resolution: '15',
            title: '15m',
        },
        {
            resolution: '30',
            title: '30m',
        },
        {
            resolution: '60',
            title: '1h',
        },
        {
            resolution: '120',
            title: '2h',
        },
        {
            resolution: '240',
            title: '4h',
        },
        {
            resolution: '360',
            title: '6h',
        },
        {
            resolution: '720',
            title: '12h',
        },
        {
            resolution: '1D',
            title: 'D',
        },
        {
            resolution: '1W',
            title: 'W',
        },
        {
            resolution: '1M',
            title: 'M',
        },
    ];

    const candleBars: CandleBars = {
        0: {
            id: 'Bars',
            icon: <BarChart stroke={colors.text.secondary} />,
            title: t('bars'),
            type: 0,
        },
        1: {
            id: 'Candles',
            icon: <CandleChart stroke={colors.text.secondary} />,
            title: t('candles'),
            type: 1,
        },
        9: {
            id: 'Hollow Candles',
            icon: <HollowChart stroke={colors.text.secondary} fill={colors.text.secondary} />,
            title: t('hollow_candles'),
            type: 9,
        },
        13: {
            id: 'Columns',
            icon: <ColumnsChart stroke={colors.text.secondary} />,
            title: t('columns'),
            type: 13,
        },
        2: {
            id: 'Line',
            icon: <LineChart stroke={colors.text.secondary} />,
            title: t('line'),
            type: 2,
        },
        3: {
            id: 'Area',
            icon: <AreaChart stroke={colors.text.secondary} fill={colors.text.secondary} />,
            title: t('area'),
            type: 3,
        },
        10: {
            id: 'Baseline',
            icon: <BaselineChart stroke={colors.text.secondary} />,
            title: t('baseline'),
            type: 10,
        },
        12: {
            id: 'High-low',
            icon: <HighLowChart stroke={colors.text.secondary} />,
            title: t('high_low'),
            type: 12,
        },
        8: {
            id: 'Heiken Ashi',
            icon: <HeikinAshiChart stroke={colors.text.secondary} />,
            title: t('heikin_ashi'),
            type: 8,
        },
    };

    useEffect(() => {
        setCurrentFavoriteIntervals(favoriteIntervals);
    }, [favoriteIntervals]);

    const executeAction = (actionId: string) => {
        const chart = tvWidgetRef?.current.chart();
        if (actionId === 'fullscreen') {
            tvWidgetRef?.current.startFullscreen();
        } else if (actionId === 'screenshot') {
            tvWidgetRef?.current.takeScreenshot();
        } else {
            chart.executeActionById(actionId);
        }
    };

    const changeChartType = (chartId: number) => {
        const chart = tvWidgetRef?.current.chart();
        chart.setChartType(chartId);
        dispatch(
            updateChartType({
                type:
                    tradeType === TRADING_TYPE.DERIVATIVES ? 'derivativeSettings' : 'spotSettings',
                chartId: chartId,
            }),
        );
    };

    const updateResolution = (resolution: ResolutionString) => {
        setIsIntervalDropdownVisible(false);
        const chart = tvWidgetRef?.current.chart();

        cancelTopicSubscription(SOCKET_SUB_TYPE.TRADE_DATA, ['indexKline', 'kline']);
        chart.setResolution(resolution, () => {
            setIsIntervalDropdownVisible(true);
        });
    };

    const updateFavoriteChartId = (_event: MouseEvent<SVGSVGElement>, id: string) => {
        _event.stopPropagation();
        dispatch(
            updateFavoriteChartTypes({
                type:
                    tradeType === TRADING_TYPE.DERIVATIVES ? 'derivativeSettings' : 'spotSettings',
                chartType: id,
            }),
        );
    };

    const editFavoriteIntervals = (resolution: ResolutionString) => {
        if (currentFavoriteIntervals.includes(resolution)) {
            setCurrentFavoriteIntervals(
                currentFavoriteIntervals.filter((item) => item !== resolution),
            );
        } else {
            setCurrentFavoriteIntervals([...currentFavoriteIntervals, resolution]);
        }
    };

    const saveFavoriteIntervals = () => {
        dispatch(
            updateFavoriteIntervals({
                type:
                    tradeType === TRADING_TYPE.DERIVATIVES ? 'derivativeSettings' : 'spotSettings',
                resolutions: currentFavoriteIntervals,
            }),
        );
        setIsEditInterval(false);
    };

    return (
        <StyledChartHeader
            styles={
                (activeTab && activeTab != 0) || (activeChart && activeChart != 'chart')
                    ? { display: 'none' }
                    : !tvWidgetRef?.current._ready
                    ? { visibility: 'hidden' }
                    : {}
            }
        >
            <ChartMenu>
                <ChartIntervals isIntervalDropdownVisible={isIntervalDropdownVisible}>
                    <FlexContainer styles={{ gap: '16px' }}>
                        {intervals.map((item, index) => {
                            return (
                                favoriteIntervals.indexOf(item.resolution as ResolutionString) !==
                                    -1 && (
                                    <FavoriteInterval
                                        key={index}
                                        onClick={() =>
                                            updateResolution(item.resolution as ResolutionString)
                                        }
                                        color={
                                            item.resolution === chartInterval ? colors.primary : ''
                                        }
                                    >
                                        {item.title}
                                    </FavoriteInterval>
                                )
                            );
                        })}
                    </FlexContainer>
                    <FlexContainer>
                        <DropDownArrow stroke={colors.text.tertiary} />
                        <IntervalResolutionDropDown>
                            <FlexContainer
                                styles={{ width: '100%', justifyContent: 'space-between' }}
                            >
                                <IntervalTitle>{t('pinned')}</IntervalTitle>
                                <IntervalTitle
                                    color={colors.primary}
                                    className="clickable"
                                    onClick={() =>
                                        !isEditInterval
                                            ? setIsEditInterval(!isEditInterval)
                                            : saveFavoriteIntervals()
                                    }
                                >
                                    {t(isEditInterval ? 'save' : 'edit')}
                                </IntervalTitle>
                            </FlexContainer>
                            <IntervalContainer>
                                {intervals.map((item, index) => {
                                    return (
                                        currentFavoriteIntervals.indexOf(
                                            item.resolution as ResolutionString,
                                        ) !== -1 && (
                                            <Interval
                                                key={index}
                                                className={
                                                    item.resolution === chartInterval
                                                        ? 'active'
                                                        : ''
                                                }
                                                onClick={() =>
                                                    !isEditInterval &&
                                                    updateResolution(
                                                        item.resolution as ResolutionString,
                                                    )
                                                }
                                            >
                                                {item.title}
                                                {isEditInterval && (
                                                    <Minus
                                                        fill={colors.text.tertiary}
                                                        stroke={colors.background.secondary.v1}
                                                        onClick={() =>
                                                            editFavoriteIntervals(
                                                                item.resolution as ResolutionString,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Interval>
                                        )
                                    );
                                })}
                            </IntervalContainer>
                            <IntervalTitle>{t('available')}</IntervalTitle>
                            <IntervalContainer>
                                {intervals.map((item, index) => {
                                    return (
                                        currentFavoriteIntervals.indexOf(
                                            item.resolution as ResolutionString,
                                        ) === -1 && (
                                            <Interval
                                                key={index}
                                                className={
                                                    item.resolution === chartInterval
                                                        ? 'active'
                                                        : ''
                                                }
                                                onClick={() =>
                                                    !isEditInterval &&
                                                    updateResolution(
                                                        item.resolution as ResolutionString,
                                                    )
                                                }
                                            >
                                                {item.title}
                                                {isEditInterval && (
                                                    <Plus
                                                        fill={colors.text.tertiary}
                                                        stroke={colors.background.secondary.v1}
                                                        onClick={() =>
                                                            editFavoriteIntervals(
                                                                item.resolution as ResolutionString,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Interval>
                                        )
                                    );
                                })}
                            </IntervalContainer>
                        </IntervalResolutionDropDown>
                    </FlexContainer>
                </ChartIntervals>
                <Bar />
                <CandleBarTypes>
                    {Object.values(candleBars).map((item, index) => {
                        return (
                            favoriteChartTypes.indexOf(item.id) !== -1 && (
                                <FlexContainer
                                    onClick={() => changeChartType(item.type)}
                                    key={index}
                                >
                                    {item.icon}
                                </FlexContainer>
                            )
                        );
                    })}
                    <FlexContainer className={'active'}>
                        {candleBars[activeChartType].icon}
                    </FlexContainer>
                    <FlexContainer>
                        <DropDownArrow stroke={colors.text.tertiary} />
                        <CandleBarDropdown>
                            {Object.values(candleBars).map((item, index) => {
                                return (
                                    <CandleBarDropdownItem
                                        key={index}
                                        className={item.type === activeChartType ? 'active' : ''}
                                        onClick={() => changeChartType(item.type)}
                                    >
                                        <FlexContainer>
                                            {item.icon}
                                            {item.title}
                                        </FlexContainer>
                                        {favoriteChartTypes.indexOf(item.id) !== -1 ? (
                                            <Favorites
                                                fill={colors.alert.yellow}
                                                stroke={colors.alert.yellow}
                                                className={'favorites'}
                                                onClick={(e) => updateFavoriteChartId(e, item.id)}
                                            />
                                        ) : (
                                            <Favorite
                                                onClick={(e) => updateFavoriteChartId(e, item.id)}
                                            />
                                        )}
                                    </CandleBarDropdownItem>
                                );
                            })}
                        </CandleBarDropdown>
                    </FlexContainer>
                </CandleBarTypes>
                <Bar />
                <ChartIndicators onClick={() => executeAction('insertIndicator')}>
                    <ChartIndicator stroke={colors.text.secondary} />
                    <span>{t('chart_indicators')}</span>
                </ChartIndicators>
            </ChartMenu>
            <FlexContainer styles={{ gap: '10px' }}>
                <ChartSetting
                    stroke={colors.text.secondary}
                    onClick={() => executeAction('chartProperties')}
                    className={'clickable'}
                />
                <ChartExpand
                    stroke={colors.text.secondary}
                    onClick={() => executeAction('fullscreen')}
                    className={'clickable'}
                />

                <ChartCamera
                    stroke={colors.text.secondary}
                    onClick={() => executeAction('screenshot')}
                    className={'clickable'}
                />
            </FlexContainer>
        </StyledChartHeader>
    );
};

export default ChartHeader;
